import { useRef, useEffect } from 'react';

// WeakMap for memoization
const dataMap = new WeakMap();

/**
 * Normalizes a Float32Array to an array of specified length for visualization
 * Supports memoization to cache results for identical inputs
 */
const normalizeArray = (data, m, downsamplePeaks = false, memoize = false) => {
  let cache, mKey, dKey;
  if (memoize) {
    mKey = m.toString();
    dKey = downsamplePeaks.toString();
    cache = dataMap.has(data) ? dataMap.get(data) : {};
    dataMap.set(data, cache);
    cache[mKey] = cache[mKey] || {};
    if (cache[mKey][dKey]) {
      return cache[mKey][dKey];
    }
  }
  const n = data.length;
  const result = new Array(m);
  if (m <= n) {
    // Downsampling
    result.fill(0);
    const count = new Array(m).fill(0);
    for (let i = 0; i < n; i++) {
      const index = Math.floor(i * (m / n));
      if (downsamplePeaks) {
        // Take highest amplitude in the set
        result[index] = Math.max(result[index], Math.abs(data[i]));
      } else {
        result[index] += Math.abs(data[i]);
      }
      count[index]++;
    }
    if (!downsamplePeaks) {
      for (let i = 0; i < result.length; i++) {
        result[i] = result[i] / count[i];
      }
    }
  } else {
    // Upsampling with linear interpolation
    for (let i = 0; i < m; i++) {
      const index = (i * (n - 1)) / (m - 1);
      const low = Math.floor(index);
      const high = Math.ceil(index);
      const t = index - low;
      if (high >= n) {
        result[i] = data[n - 1];
      } else {
        result[i] = data[low] * (1 - t) + data[high] * t;
      }
    }
  }
  if (memoize) {
    cache[mKey][dKey] = result;
  }
  return result;
};

/**
 * React component to render audio visualization as bars on a canvas
 */
export const WavRenderer = ({
  audioData = new Float32Array(),
  color = 'black',
  pointCount = 0,
  barWidth = 0,
  barSpacing = 0,
  center = false,
  width = 600,
  height = 200,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx || !audioData.length) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate pointCount if not provided
    let adjustedPointCount = Math.floor(
      Math.min(
        pointCount,
        (canvas.width - barSpacing) / (Math.max(barWidth, 1) + barSpacing)
      )
    );
    if (!adjustedPointCount) {
      adjustedPointCount = Math.floor(
        (canvas.width - barSpacing) / (Math.max(barWidth, 1) + barSpacing)
      );
    }

    // Calculate barWidth if not provided
    const adjustedBarWidth = barWidth || (canvas.width - barSpacing) / adjustedPointCount - barSpacing;

    // Normalize audio data
    const points = normalizeArray(audioData, adjustedPointCount, true);

    // Draw bars
    for (let i = 0; i < adjustedPointCount; i++) {
      const amplitude = Math.abs(points[i]);
      const height = Math.max(1, amplitude * canvas.height);
      const x = barSpacing + i * (adjustedBarWidth + barSpacing);
      const y = center ? (canvas.height - height) / 2 : canvas.height - height;
      ctx.fillStyle = color;
      ctx.fillRect(x, y, adjustedBarWidth, height);
    }
  }, [audioData, color, pointCount, barWidth, barSpacing, center, width, height]);

  return <canvas ref={canvasRef} width={width} height={height} />;
};